import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useRows } from '@/hooks/useLive';
import { useSiteFilter } from '@/hooks/useSiteFilter';
import {
  activityTypesFor, allocatedHours, engineHours, lastClosingMeter, longerShift, loadDraft, newDraft, payloadFor, productiveCategory,
  saveDiary, settingNumber, syncProductiveToMeter, validateDiary, type DiaryDraft, type DiaryIssue,
} from '@/lib/diary';
import { hours as fmtHours, humanise, isoDate, num } from '@/lib/format';
import { maxPerShift, meterSuffix, unitOf, type MeterUnit } from '@/lib/meter';
import { useAuthStore } from '@/stores/authStore';
import { useSyncStore } from '@/stores/syncStore';
import { toast } from '@/stores/toastStore';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import { NumberInput, SelectInput, TextArea, TextInput } from '@/components/ui/Field';
import { LockNotice } from '@/components/ui/LockNotice';
import { diaryLock } from '@/lib/editability';
import { TimeAllocation } from './TimeAllocation';
import { Activities } from './Activities';
import { QUARRY_SITE_ID, diarySiteOf } from '@/lib/fleet';
import { siteRuns } from '@/lib/operations';
import type {
  ActivityType, DiaryStatus, Machine, Operator, Setting, Shift, Site, TimeCategory,
} from '@/types/models';

const STATUS_TONE: Record<DiaryStatus, 'grey' | 'blue' | 'green' | 'red'> = {
  draft: 'grey',
  submitted: 'blue',
  approved: 'green',
  rejected: 'red',
};

export function DiaryEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const machines = useRows<Machine>('machines', 'fleet_no');
  const shifts = useRows<Shift>('shifts', 'sort_order');
  const operators = useRows<Operator>('operators', 'full_name');
  const categories = useRows<TimeCategory>('time_categories', 'sort_order');
  const activityTypes = useRows<ActivityType>('activity_types', 'sort_order');
  const locations = useRows<Record<string, unknown>>('locations', 'code');
  const blasts = useRows<Record<string, unknown>>('blasts', 'blast_no');
  const materials = useRows<Record<string, unknown>>('materials', 'code');
  const products = useRows<Record<string, unknown>>('products', 'code');
  const machineTypes = useRows<Record<string, unknown>>('machine_types', 'code');
  const settings = useRows<Setting>('settings');
  const sites = useRows<Site>('sites', 'name');
  const siteOk = useSiteFilter();

  const profile = useAuthStore((s) => s.profile);
  const canApprove = !!profile?.role && ['supervisor', 'manager', 'admin'].includes(profile.role);
  const online = useSyncStore((s) => s.online);

  const [draft, setDraft] = useState<DiaryDraft | null>(null);
  const [issues, setIssues] = useState<DiaryIssue[]>([]);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const tolerance = settingNumber(settings, 'diary_hours_tolerance', 0.5);

  /* ---------------------------------------------------- load / create */
  // Load once per diary, then leave it alone.
  //
  // This effect used to re-run whenever the machine or shift lists changed
  // identity — which they do on every sync that touches those tables. And
  // every diary saved anywhere bumps its machine's hour meter on the
  // server, which arrives in the next sync. So a supervisor typing a diary
  // while any tablet synced had the draft reloaded from the local store
  // underneath them: an existing diary lost its unsaved edits, and a new
  // one was replaced with a blank. The lists are still needed to BUILD a
  // new draft; they must never trigger a rebuild of one already open.
  const loadedFor = useRef<string | null>(null);

  useEffect(() => {
    const key = id && id !== 'new' ? `diary:${id}` : `new:${params.toString()}`;
    if (loadedFor.current === key) return;

    let cancelled = false;
    (async () => {
      if (id && id !== 'new') {
        const existing = await loadDraft(id);
        if (cancelled) return;
        if (!existing) {
          setLoadError('That diary is not on this device yet. Sync and try again.');
          return;
        }
        setDraft(existing);
        loadedFor.current = key;
        return;
      }

      if (!machines || !shifts || !categories) return;   // not loaded yet — try again when they are
      const date = params.get('date') ?? isoDate();
      const machine = machines.find((m) => m.id === params.get('machine'));
      const targetSite = machine ? diarySiteOf(machine) : useAuthStore.getState().branchId() ?? QUARRY_SITE_ID;
      const ownShifts = shifts.filter((s) => (s.site_id || QUARRY_SITE_ID) === targetSite && s.is_active);
      const shift = ownShifts.find((s) => s.id === params.get('shift')) ?? ownShifts[0];
      const last = machine ? await lastClosingMeter(machine.id, date, unitOf(machine)) : null;
      if (cancelled) return;
      // A new diary books 0,5 h to Service by default (SAF-84).
      const service = categories.find((c) => c.code === 'SERVICE' && !c.deleted_at && c.is_active !== false);
      setDraft(newDraft({ date, machine, shift, lastClosingMeter: last, serviceCategoryId: service?.id ?? null }));
      loadedFor.current = key;
    })();
    return () => { cancelled = true; };
  }, [id, params, machines, shifts, categories]);

  const machine = machines?.find((m) => m.id === draft?.machine_id);
  // 0068: an ADT's own entries carry production; a loader's do not.
  const machineHauling = !!machineTypes?.find((t) => t.id === machine?.machine_type_id)?.is_hauling;
  const atBranch = !!machine && (sites ?? []).find((s) => s.id === diarySiteOf(machine))?.kind === 'branch';
  // A saved diary keeps the unit it was captured in; a new one takes the
  // machine's (hour meter or odometer, 0034).
  const unit: MeterUnit = draft?.meter_unit ?? unitOf(machine);
  const defaultPayload = useMemo(() => payloadFor(machine, settings), [machine, settings]);
  // Approved means locked, for everyone. An approver used to see every field
  // unlocked on an approved diary, edit it, and press Re-open — which never
  // re-opened it on the server and replaced its hours and production
  // underneath (fixed in 0028). Now a supervisor re-opens first, then edits.
  //
  // 0046: the rest of the server's rule is shown too. An operator may change
  // only their own diary, and only while it is a draft; before, the screen
  // let them edit a submitted one and the server refused it out of sight.
  const lockReason = draft
    ? diaryLock({
        status: draft.status,
        saved: Boolean(id && id !== 'new'),
        createdBy: draft.created_by,
        userId: profile?.id,
        isLead: canApprove,
      })
    : null;
  const locked = lockReason !== null;

  /* ------------------------------------------------------------ edit */
  // SAF-84: on an hour meter the productive hours always follow the
  // readings (closing − opening). Every edit re-applies it, so changing a
  // reading moves Productive with it.
  function withMeterProductive(d: DiaryDraft): DiaryDraft {
    const allocations = syncProductiveToMeter(d.allocations, categories, d, d.meter_unit ?? unitOf(machine));
    return allocations === d.allocations ? d : { ...d, allocations };
  }

  function patch(p: Partial<DiaryDraft>) {
    if ('opening_hour_meter' in p || 'diary_date' in p) machineChange.current += 1;
    setDraft((d) => (d && !locked ? withMeterProductive({ ...d, ...p }) : d ? { ...d, ...p } : d));
  }

  /** Changing machine pulls its last closing reading forward. */
  const machineChange = useRef(0);
  async function onMachineChange(machineId: string | null) {
    if (!draft) return;
    const m = machines?.find((x) => x.id === machineId);
    const targetSite = m ? diarySiteOf(m) : profile?.home_site_id ?? QUARRY_SITE_ID;
    const currentShift = shifts?.find((s) => s.id === draft.shift_id);
    const nextShift = (currentShift?.site_id || QUARRY_SITE_ID) === targetSite ? currentShift
      : shifts?.find((s) => (s.site_id || QUARRY_SITE_ID) === targetSite && s.is_active && s.code === currentShift?.code)
        ?? shifts?.find((s) => (s.site_id || QUARRY_SITE_ID) === targetSite && s.is_active);
    const nextOperator = operators?.find((o) => o.id === draft.operator_id && (o.site_id || QUARRY_SITE_ID) === targetSite)?.id ?? null;
    patch({
      machine_id: machineId ?? '', operator_id: nextOperator,
      shift_id: nextShift?.id ?? '', scheduled_hours: nextShift?.scheduled_hours ?? draft.scheduled_hours,
      opening_hour_meter: m?.current_hour_meter ?? null,
      closing_hour_meter: null,
      meter_unit: unitOf(m),
    });
    const request = ++machineChange.current;
    const last = machineId ? await lastClosingMeter(machineId, draft.diary_date, unitOf(m)) : null;
    // Another selection, a date edit or a manually typed opening reading wins.
    if (request !== machineChange.current) return;
    if (last != null) patch({ opening_hour_meter: last });
  }

  function onShiftChange(shiftId: string | null) {
    const s = shifts?.find((x) => x.id === shiftId);
    patch({ shift_id: shiftId ?? '', scheduled_hours: s?.scheduled_hours ?? draft?.scheduled_hours ?? 10 });
  }

  /* ------------------------------------------------------------ save */
  async function reopen() {
    if (!draft) return;
    const next = { ...draft, status: 'draft' as DiaryStatus };
    setBusy(true);
    try {
      await saveDiary(next, profile?.id ?? null);
      setDraft(next);
      toast.success(online ? 'Re-opened — you can edit it now' : 'Re-opened — will sync when back online');
    } catch (err) {
      toast.error((err as Error).message ?? 'Could not re-open');
    } finally {
      setBusy(false);
    }
  }

  async function persist(status: DiaryStatus) {
    if (!draft) return;
    // An older diary saved before productive followed the meter is put
    // right when it is saved again.
    const next = withMeterProductive({ ...draft, status });
    const found = await validateDiary(next, {
      settings, categories, activityTypes, meterUnit: unit, machineTypeId: machine?.machine_type_id,
      machineHauling, atBranch,
    });
    setIssues(found);
    if (found.some((i) => i.blocking)) {
      toast.error('Fix the highlighted problems before saving');
      return;
    }

    setBusy(true);
    try {
      await saveDiary(next, profile?.id ?? null);
      setDraft({ ...next, created_by: next.created_by ?? profile?.id ?? null, client_rev: next.client_rev + 1 });
      toast.success(
        status === 'draft' ? 'Saved as draft'
          : status === 'submitted' ? (online ? 'Submitted' : 'Submitted — will sync when back online')
          : status === 'approved' ? 'Approved'
          : 'Re-opened',
      );
      if (status !== 'draft') navigate('/diary');
    } catch (err) {
      toast.error((err as Error).message ?? 'Could not save');
    } finally {
      setBusy(false);
    }
  }

  /* --------------------------------------------------------- render */
  if (loadError) {
    return (
      <div className="placeholder">
        <h2>Diary not available offline</h2>
        <p className="muted">{loadError}</p>
        <Button variant="secondary" onClick={() => navigate('/diary')}>Back to the diary</Button>
      </div>
    );
  }

  if (!draft || !categories || !activityTypes || !machines || !shifts) {
    return <div className="centered"><span className="spinner" /><span className="small">Loading…</span></div>;
  }

  const eng = engineHours(draft);
  const engBad = eng != null && (eng < 0 || eng > maxPerShift(unit));
  const booked = allocatedHours(draft.allocations);
  const meterOver = unit === 'hours' && eng != null && eng > draft.scheduled_hours;
  const bookedOver = booked > draft.scheduled_hours;
  const offerShift = longerShift(unit === 'hours' ? eng : null, booked);
  const blocking = issues.filter((i) => i.blocking);
  const warnings = issues.filter((i) => !i.blocking);

  const locationOptions = (locations ?? []).map((l) => ({
    value: String(l.id), label: `${l.code} — ${l.name}`,
  }));
  const blastOptions = (blasts ?? [])
    .filter((b) => ['blasted', 'in_production'].includes(String(b.status)))
    .map((b) => ({ value: String(b.id), label: String(b.blast_no) }));
  // 0053: a diary is for a vehicle at the quarry or at a concrete branch.
  const diarySites = new Set<string>([
    ...(siteOk(QUARRY_SITE_ID) ? [QUARRY_SITE_ID] : []),
    ...(sites ?? []).filter((s) => s.kind === 'branch' && s.is_active && !s.deleted_at && siteOk(s.id)).map((s) => s.id),
  ]);
  // 0052: the concrete branches, for concrete delivered or pumped. Every
  // active branch: the diary is captured at the quarry for any of them.
  const branchOptions = (sites ?? [])
    .filter((s) => s.kind === 'branch' && siteRuns(s, 'concrete') && s.is_active && !s.deleted_at && siteOk(s.id))
    .map((s) => ({ value: s.id, label: s.name }));

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <div className="record-header">
        <span className="record-header__title">
          {machine ? machine.fleet_no : 'New diary entry'}
        </span>
        <StatusChip tone={STATUS_TONE[draft.status]}>{humanise(draft.status)}</StatusChip>
        {!online && <StatusChip tone="amber">Offline — saved on this device</StatusChip>}
        <span className="spacer" />
        <Button variant="ghost" onClick={() => navigate('/diary')}>Back</Button>
      </div>

      {(blocking.length > 0 || warnings.length > 0) && (
        <Panel title="Check before saving">
          <div className="issue-list">
            {blocking.map((i, n) => (
              <div className="issue issue--block" key={`b${n}`}>⛔ {i.message}</div>
            ))}
            {warnings.map((i, n) => (
              <div className="issue issue--warn" key={`w${n}`}>⚠ {i.message}</div>
            ))}
          </div>
        </Panel>
      )}

      {lockReason && (
        <LockNotice tone={draft.status === 'approved' ? 'ok' : 'warn'}>{lockReason}</LockNotice>
      )}

      {/* One native fieldset locks every control inside it — the header and
          the allocation and activity panels alike. Those panels were never
          told about the lock, so an approved diary's hours and loads stayed
          editable on screen. The action buttons sit outside, so Re-open
          still works. */}
      <fieldset disabled={locked} className="fieldset-plain col" style={{ gap: 'var(--sp-3)' }}>
      <Panel title="Shift">
        <div className="diary-header">
          <TextInput
            id="d-date" label="Date" type="date" required
            value={draft.diary_date}
            disabled={locked}
            onChange={(v) => patch({ diary_date: v })}
          />
          <SelectInput
            id="d-machine" label="Machine" required
            value={draft.machine_id || null}
            disabled={locked}
            options={machines.filter((m) => (m.is_active && diarySites.has(diarySiteOf(m)))
                || m.id === draft.machine_id)
              .map((m) => {
                const site = diarySiteOf(m);
                const name = site === QUARRY_SITE_ID ? '' : sites?.find((s) => s.id === site)?.name;
                return { value: m.id, label: name ? `${m.fleet_no} · ${name}` : m.fleet_no };
              })}
            onChange={(v) => void onMachineChange(v)}
          />
          <SelectInput
            id="d-shift" label="Shift" required
            value={draft.shift_id || null}
            disabled={locked}
            options={shifts.filter((s) => (s.is_active && (s.site_id || QUARRY_SITE_ID) === (machine ? diarySiteOf(machine) : profile?.home_site_id ?? QUARRY_SITE_ID))
                || s.id === draft.shift_id).map((s) => ({
              value: s.id, label: `${s.name} (${fmtHours(s.scheduled_hours)} h)`,
            }))}
            onChange={onShiftChange}
          />
          <NumberInput
            id="d-shift-hours" label="Shift length" required suffix="h" min={0.5} max={24} step={0.5}
            value={draft.scheduled_hours}
            onChange={(v) => patch({ scheduled_hours: v ?? 0 })}
            help="From the shift. Change it for this day if the shift ran over or was cut short."
          />
          <SelectInput
            id="d-operator" label="Operator"
            value={draft.operator_id}
            disabled={locked}
            options={(operators ?? []).filter((o) => (o.is_active && (o.site_id || QUARRY_SITE_ID) === (machine ? diarySiteOf(machine) : profile?.home_site_id ?? QUARRY_SITE_ID))
                || o.id === draft.operator_id).map((o) => ({
              value: o.id, label: o.full_name,
            }))}
            onChange={(v) => patch({ operator_id: v })}
          />
        </div>

        <div className="diary-header">
          <NumberInput
            id="d-open" label={unit === 'km' ? 'Opening odometer' : 'Opening hour meter'} required
            suffix={meterSuffix(unit)} min={0} step={unit === 'km' ? 1 : 0.1}
            value={draft.opening_hour_meter}
            disabled={locked}
            onChange={(v) => patch({ opening_hour_meter: v })}
            help="Pre-filled from the last closing reading."
          />
          <NumberInput
            id="d-close" label={unit === 'km' ? 'Closing odometer' : 'Closing hour meter'} required
            suffix={meterSuffix(unit)} min={0} step={unit === 'km' ? 1 : 0.1}
            value={draft.closing_hour_meter}
            disabled={locked}
            onChange={(v) => patch({ closing_hour_meter: v })}
          />
          <div className={`engine-hours${engBad ? ' engine-hours--bad' : ''}`}>
            <span className="engine-hours__value">
              {eng == null ? '—' : unit === 'km' ? num(eng, 0) : fmtHours(eng)}
            </span>
            <span className="engine-hours__label">{unit === 'km' ? 'km driven' : 'engine hours'}</span>
          </div>
        </div>

        {/* A machine that ran longer than the shift worked overtime. The only
            honest record of that is a longer shift for the day — not a wider
            tolerance, which switches the check off for every diary on site.
            0049: offered too when more time is booked than the shift holds,
            which is now refused — the over-booking refusal points here. */}
        {!locked && (meterOver || bookedOver) && offerShift > draft.scheduled_hours && (
          <div className="issue issue--warn" style={{ marginTop: 'var(--sp-2)', alignItems: 'center' }}>
            <span className="small">
              {meterOver
                ? <>The meter shows {fmtHours(eng)} h — longer than the {fmtHours(draft.scheduled_hours)} h
                    shift. If the shift ran over, record the longer shift for this day.</>
                : <>{fmtHours(booked)} h is booked — more than the {fmtHours(draft.scheduled_hours)} h
                    shift. Take the extra off below, or if the machine really worked longer, record
                    the longer shift for this day.</>}
            </span>
            <span className="spacer" />
            <Button variant="secondary" style={{ minHeight: 30 }}
              onClick={() => patch({ scheduled_hours: offerShift })}>
              Shift was {fmtHours(offerShift)} h
            </Button>
          </div>
        )}
      </Panel>

      <Panel title="How the shift was used">
        <TimeAllocation
          categories={categories.filter((c) => c.is_active)}
          allocations={draft.allocations}
          scheduledHours={draft.scheduled_hours}
          tolerance={tolerance}
          fixedCategoryId={unit === 'hours' ? productiveCategory(categories)?.id : undefined}
          fixedHint="Engine hours from the meter"
          onChange={(next) => patch({ allocations: next })}
        />
      </Panel>

      <Panel title="What the machine did">
        <Activities
          activityTypes={activityTypesFor(activityTypes.filter((t) => t.is_active), machine)}
          allActivityTypes={activityTypes}
          activities={draft.activities}
          defaultPayload={defaultPayload}
          locations={locationOptions}
          blasts={blastOptions}
          branches={branchOptions}
          materials={(materials ?? []).filter((m) => m.is_active !== false && !m.deleted_at)
            .map((m) => ({ value: String(m.id), label: String(m.name) }))}
          products={(products ?? []).filter((x) => x.is_active !== false && !x.deleted_at)
            .map((x) => ({ value: String(x.id), label: String(x.name), material_id: (x.material_id as string | null) ?? null }))}
          machineHauling={machineHauling}
          atBranch={atBranch}
          onChange={(next) => patch({ activities: next })}
        />
      </Panel>

      <Panel title="Comments">
        <TextArea
          id="d-comments" label=""
          value={draft.comments ?? ''}
          onChange={(v) => patch({ comments: v || null })}
        />
      </Panel>
      </fieldset>

      <div className="row" style={{ justifyContent: 'flex-end', gap: 'var(--sp-2)', paddingBottom: 'var(--sp-5)' }}>
        {draft.status === 'approved' && canApprove && (
          <Button variant="secondary" onClick={() => void reopen()} loading={busy}>
            Re-open to edit
          </Button>
        )}
        {!locked && (
          <>
            <Button variant="secondary" onClick={() => void persist('draft')} loading={busy}>
              Save draft
            </Button>
            <Button onClick={() => void persist('submitted')} loading={busy}>
              Submit
            </Button>
          </>
        )}
        {draft.status === 'submitted' && canApprove && (
          <Button onClick={() => void persist('approved')} loading={busy}>Approve</Button>
        )}
      </div>
    </div>
  );
}
